import { ASSIGNABLE_WORKSPACE_ROLE_VALUES, ROLE_LABELS, type WorkspaceRole } from "@/constants/roles";
import { PageHeader } from "@/components/shared/page-header";
import { EmptyState, ErrorState, LoadingState } from "@/components/shared/states";
import { RoleBadge } from "@/components/shared/status-badge";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog } from "@/components/ui/dialog";
import { Field, Input, Select } from "@/components/ui/field";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  useInviteMember,
  useMembers,
  useRemoveMember,
  useUpdateMemberRole,
} from "@/features/workspaces/api";
import { formatDate } from "@/lib/format";
import { useWorkspace } from "@/providers/workspace-provider";
import { UserPlus } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

/** `/w/:slug/users` — workspace members, invitations and role assignment. */
export const UsersPage = () => {
  const { workspace } = useWorkspace();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<WorkspaceRole>(ASSIGNABLE_WORKSPACE_ROLE_VALUES[1]);

  const membersQuery = useMembers(workspace.id);
  const inviteMember = useInviteMember(workspace.id);
  const updateRole = useUpdateMemberRole(workspace.id);
  const removeMember = useRemoveMember(workspace.id);

  const onInvite = async () => {
    try {
      await inviteMember.mutateAsync({ email: email.trim(), role });
      toast.success("Invitation created. Share the token with your teammate.");
      setEmail("");
      setInviteOpen(false);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not create the invitation.");
    }
  };

  const members = membersQuery.data?.members ?? [];
  const invitations = membersQuery.data?.invitations ?? [];

  return (
    <>
      <PageHeader
        title="Members"
        description={`Plan: ${membersQuery.data?.plan ?? workspace.plan}`}
        actions={
          <Button onClick={() => setInviteOpen(true)}>
            <UserPlus className="size-4" aria-hidden />
            Invite member
          </Button>
        }
      />

      <Card>
        <CardContent className="p-0">
          {membersQuery.isLoading ? <LoadingState /> : null}
          {membersQuery.isError ? (
            <ErrorState
              description={
                membersQuery.error instanceof Error ? membersQuery.error.message : undefined
              }
              onRetry={() => void membersQuery.refetch()}
            />
          ) : null}
          {!membersQuery.isLoading && members.length === 0 ? (
            <EmptyState title="No members" description="Invite a teammate to collaborate." />
          ) : null}
          {members.length > 0 ? (
            <div className="p-4">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Member</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Joined</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {members.map((member) => (
                    <TableRow key={member.id}>
                      <TableCell>
                        <p className="font-medium">{member.profile?.display_name ?? "—"}</p>
                        <p className="text-xs text-muted-foreground">{member.profile?.email ?? ""}</p>
                      </TableCell>
                      <TableCell>
                        <RoleBadge role={member.role} />
                      </TableCell>
                      <TableCell>
                        <Badge variant={member.status === "active" ? "success" : "outline"}>
                          {member.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {formatDate(member.created_at)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Select
                            aria-label={`Role for ${member.profile?.email ?? member.id}`}
                            value={member.role}
                            disabled={member.role === "workspace_owner"}
                            className="w-40"
                            onChange={async (event) => {
                              try {
                                await updateRole.mutateAsync({
                                  memberId: member.id,
                                  role: event.target.value as WorkspaceRole,
                                });
                                toast.success("Role updated.");
                              } catch (error) {
                                toast.error(
                                  error instanceof Error ? error.message : "Could not update role.",
                                );
                              }
                            }}
                          >
                            <option value={member.role}>{ROLE_LABELS[member.role]}</option>
                            {ASSIGNABLE_WORKSPACE_ROLE_VALUES.map((value) => (
                              <option key={value} value={value}>
                                {ROLE_LABELS[value]}
                              </option>
                            ))}
                          </Select>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-danger"
                            disabled={member.role === "workspace_owner"}
                            onClick={async () => {
                              try {
                                await removeMember.mutateAsync(member.id);
                                toast.success("Member removed.");
                              } catch (error) {
                                toast.error(
                                  error instanceof Error
                                    ? error.message
                                    : "Could not remove member.",
                                );
                              }
                            }}
                          >
                            Remove
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : null}
        </CardContent>
      </Card>

      {invitations.length > 0 ? (
        <Card>
          <CardContent className="space-y-2 p-4">
            <p className="text-sm font-medium">Pending invitations</p>
            {invitations.map((invitation) => (
              <div key={invitation.id} className="flex items-center justify-between text-sm">
                <span>
                  {invitation.email} · {ROLE_LABELS[invitation.role]}
                </span>
                <code className="rounded bg-muted px-2 py-0.5 text-xs">{invitation.token}</code>
              </div>
            ))}
          </CardContent>
        </Card>
      ) : null}

      <Dialog
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        title="Invite member"
        description="The Free plan allows a single member; Pro unlocks the rest."
        footer={
          <>
            <Button variant="outline" onClick={() => setInviteOpen(false)}>
              Cancel
            </Button>
            <Button loading={inviteMember.isPending} onClick={() => void onInvite()}>
              Send invite
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <Field label="Email" htmlFor="invite-email" required>
            <Input
              id="invite-email"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </Field>
          <Field label="Role" htmlFor="invite-role">
            <Select
              id="invite-role"
              value={role}
              onChange={(event) => setRole(event.target.value as WorkspaceRole)}
            >
              {ASSIGNABLE_WORKSPACE_ROLE_VALUES.map((value) => (
                <option key={value} value={value}>
                  {ROLE_LABELS[value]}
                </option>
              ))}
            </Select>
          </Field>
        </div>
      </Dialog>
    </>
  );
};
